import { MutableRefObject } from "react";
import { FieldValues, Path, UseFormSetValue } from "react-hook-form";

export const trimStringInputs = <T extends FieldValues>(
  inputs: T,
  setValue: UseFormSetValue<T>
) => {
  Object.keys(inputs).forEach((key) => {
    const name = key as Path<T>;
    if (!!inputs[name] && typeof inputs[name] === "string") {
      setValue(name as Path<T>, inputs[name].trim());
    }
  });
};

type FieldFocusHandle = {
  focus: () => void;
};

const fieldFocusHandles = new WeakMap<object, FieldFocusHandle>();

const scrollFieldIntoView = (element: HTMLElement) => {
  const target =
    element.closest<HTMLElement>(
      ".MuiFormControl-root, .MuiTextField-root, .MuiAutocomplete-root"
    ) ?? element;

  target.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

const getFieldFocusHandle = (element: object): FieldFocusHandle => {
  const existing = fieldFocusHandles.get(element);
  if (existing) {
    return existing;
  }

  const handle: FieldFocusHandle = {
    focus: () => {
      if (element instanceof HTMLElement) {
        element.focus({ preventScroll: true });
        scrollFieldIntoView(element);
        return;
      }

      // MUI Select exposes an imperative handle, not a DOM node.
      const focusable = element as { focus?: () => void };
      focusable.focus?.();
      if (document.activeElement instanceof HTMLElement) {
        scrollFieldIntoView(document.activeElement);
      }
    },
  };

  fieldFocusHandles.set(element, handle);
  return handle;
};

/**
 * Assign a Controller `field.ref` and an optional local ref to the same node.
 * Uses a stable focus handle per node so RHF can smooth-scroll on errors
 * without recreating the ref object every render.
 */
export const setControllerFieldRef = (
  rhfRef: (instance: unknown) => void,
  element: unknown,
  extraRef?: MutableRefObject<HTMLInputElement | null>
) => {
  if (extraRef) {
    extraRef.current = element instanceof HTMLInputElement ? element : null;
  }

  if (element !== null && typeof element === "object") {
    rhfRef(getFieldFocusHandle(element));
    return;
  }

  rhfRef(element);
};
